/**
 * Pure helpers for the shared JobCategoryDetailPage (ER-11381, Organization >
 * Job Categories).
 *
 * The detail page renders one Job Category from the EIGE / European Commission
 * Gender-Neutral Job Evaluation Toolkit "4-Factor" model that the
 * AddJobCategoryWizard (ER-11356) uses to create the category: Skills,
 * Responsibility, Effort and Working Conditions, each with 0–5 subfactor levels
 * (Knowledge 0–8) and official level descriptors. Those constants and the score
 * normalisers/aggregators already live in addJobCategoryWizard.js — this page
 * reuses them instead of duplicating the toolkit data.
 *
 * The category shape consumed by the page is the wizard's submit payload plus
 * the list's row identity (see JobCategoriesList → buildRowView):
 *
 *   {
 *     id, code, name, status, unread, positionCount, updatedLabel,
 *     scores: { skills: { knowledge: 3, ... }, responsibility: {...}, ... }
 *   }
 *
 * Factor points (the `s/e/r/wc` summary shape, derived from the levels) and the
 * overall Total Score are derived here so both the summary panel and the
 * per-factor sections read from a single source. Every reported score is a
 * point total against its EIGE maximum (73 overall); percentages exist only as
 * gauge fill geometry.
 *
 * The detail page follows Figma node 47317:204060 ("JobCategoryCard") — its
 * subfactor descriptions and the "Level N." descriptor prefix differ from the
 * wizard's shorter instructional copy, so the page-level subfactor descriptions
 * live here. Level descriptors come from addJobCategoryWizard.js (EIGE §4,
 * verbatim) so both paths describe the same level identically.
 *
 * Kept framework-free so unit tests can exercise the view model without
 * mounting the SFC (mirrors AddJobCategoryWizard/addJobCategoryWizard.js).
 */
import {
  FACTORS,
  factorByKey,
  factorIsComplete,
  factorTotal,
  isScoreSet,
  maxFactorScore,
  normalizeScores,
  scorePercent,
  subfactorLevelDescription,
  subfactorLevelTooltips,
  subfactorMaxLevel,
  subfactorScoreLevels,
  wizardIsComplete,
} from "@components/AddJobCategoryWizard/addJobCategoryWizard.js";
import {
  FACTOR_SCORE_KEYS,
  displayCode,
  isDisabledRow,
  normalizePositionCount,
  normalizeStatus,
} from "@components/JobCategoriesList/jobCategoriesListRow.js";

/** Statuses the list renders as greyed rows (retired/archived). */
const GREYED_STATUSES = new Set(["retired", "archived"]);

/** The sum of every factor's maximum: (8+5+5+5+5) + (5+5+5+5) + (5+5+5) + (5+5) = 73. */
const MAX_TOTAL_SCORE = FACTORS.reduce((sum, factor) => sum + maxFactorScore(factor), 0);

/** Static EIGE subtitle shown under the category name (Figma 47317:204060). */
export const CATEGORY_SUBTITLE =
  "Job Category — scored under the EIGE/European Commission Gender-Neutral Job Evaluation Toolkit";

/**
 * The official EIGE subfactor descriptions used on the detail page (Figma).
 * These differ from the wizard's shorter instructional copy — the detail page
 * shows the formal evaluation-toolkit wording.
 */
const SUBFACTOR_DESCRIPTIONS = {
  knowledge:
    "Level of experience, formal education, training and basic skills necessary to meet job requirements.",
  interpersonalCommunication:
    "Requirement to deal with people within and outside the organisation.",
  problemSolving:
    "Level of difficulty in identifying options and exercising judgement to select the most appropriate action.",
  planningOrganisational: "Ability to plan and organise tasks effectively.",
  physicalSkills:
    "Physical and fine motor skills — manual dexterity, coordination, sensory skills.",
  people:
    "Extent to which a job involves responsibility for people — direct management, guidance, care and support of others.",
  goodsEquipment:
    "Degree of responsibility for collection, storage, retrieval, safe use and maintenance of material resources.",
  information:
    "Degree of responsibility for collecting, storing, retrieving, interpreting and maintaining information/data/files.",
  financialResources:
    "Degree of accountability for money, financial data, financial records and related decisions.",
  mental:
    "Duration and intensity of cognitive effort — concentration, attentiveness, alertness.",
  psychosocialEmotional:
    "Emotional energy required to manage psychological/interpersonal job demands.",
  physicalEffort: "Duration and intensity of physical demands on the body.",
  environment: "Nature and severity of working conditions and hazards affecting the job.",
  organisationalEnvironment:
    "Working-day duration and pattern — night shifts, irregular hours, travel, connectivity.",
};

/** Summary-gauge order in the Figma: S, E, R, WC (matching the list's gauge keys). */
const SUMMARY_GAUGE_ORDER = ["skills", "effort", "responsibility", "workingConditions"];

/** Source `scores` object of a category (defensive copy of a nested object). */
export function categoryScoresOf(category) {
  const source = category && typeof category === "object" ? category.scores : null;
  return source && typeof source === "object" ? source : null;
}

/** Normalised nested 0–5 / 0–8 scores for a category (every subfactor present). */
export function normalizeCategoryScores(category) {
  return normalizeScores(categoryScoresOf(category));
}

export function categoryName(category) {
  return String((category && typeof category === "object" && category.name) || "").trim();
}

/** Identity/status header view-model consumed by OrgObjectStatusBadge/ChatterIcon. */
export function categoryHeaderView(category) {
  const raw = category && typeof category === "object" ? category : {};
  const status = normalizeStatus(raw.status);
  const disabled = isDisabledRow(status, raw.disabled);
  const positionCount = normalizePositionCount(raw.positionCount);
  return {
    id: raw.id ?? null,
    name: categoryName(category),
    code: displayCode(raw.code),
    status,
    disabled,
    cardState: disabled ? "Disabled" : "Active",
    badgeNeutral: GREYED_STATUSES.has(status),
    unread: Number(raw.unread) > 0 ? Number(raw.unread) : 0,
    subtitle: CATEGORY_SUBTITLE,
    positionCount,
    positionLabel: `${positionCount} ${positionCount === 1 ? "position" : "positions"} attached`,
    updatedLabel: String(raw.updatedLabel || "").trim(),
  };
}

/** One factor's totals: `{ key, label, summaryLetter, complete, total, max, pct, display }`. */
export function factorTotalsView(factor, normalizedScores) {
  const max = maxFactorScore(factor);
  const complete = factorIsComplete(normalizedScores, factor);
  const total = factorTotal(normalizedScores, factor);
  const pct = complete ? scorePercent(total, max) : null;
  return {
    key: factor.key,
    label: factor.label,
    summaryLetter: factor.summaryLetter,
    complete,
    total,
    max,
    pct,
    display: complete ? `${total} / ${max}` : `– / ${max}`,
  };
}

/** The four factor totals used by the 4-Factor Score summary panel (FACTORS order). */
export function factorSummaryRows(normalizedScores) {
  return FACTORS.map((factor) => factorTotalsView(factor, normalizedScores));
}

/** Uppercase summary letter used on the Figma gauges ("S", "E", "R", "WC"). */
export function summaryLetterOf(factor) {
  const letter = String((factor && factor.summaryLetter) || "");
  return letter === "wc" ? "WC" : letter.toUpperCase();
}

/** Shown instead of a number when nothing has been scored yet. */
export const NOT_SCORED_LABEL = "Not scored";

/** True when at least one subfactor of `factor` carries a level (0 counts as scored). */
function factorHasScoredSubfactor(normalizedScores, factor) {
  return factor.subfactors.some((subfactor) =>
    isScoreSet(normalizedScores?.[factor.key]?.[subfactor.key], subfactorMaxLevel(subfactor))
  );
}

/**
 * The four 4-Factor Score summary gauges, in the Figma order (S, E, R, WC).
 * Each gauge exposes `{ key, letter, label, fullLabel, definition, complete,
 * scored, total, max, value, display }`: `display` is the factor's raw points
 * against its own EIGE maximum (e.g. "19 / 28"), or "Not scored" until one of
 * its subfactors carries a level. `value` is the 0–100 share used only to draw
 * the gauge fill; percentages are never a reported score form.
 */
export function factorSummaryGauges(normalizedScores, factorScores) {
  return SUMMARY_GAUGE_ORDER.map((key) => {
    const factor = factorByKey(key);
    const totals = factorTotalsView(factor, normalizedScores);
    const scored = factorHasScoredSubfactor(normalizedScores, factor);
    // Legacy rows without nested levels keep the API factor points; a factor
    // with any scored subfactor reports the raw sum of those levels.
    const fallback = scored
      ? null
      : pointsFromFactorScores(factorScores, factor && factor.summaryLetter);
    const total = scored ? totals.total : fallback;
    const { fullLabel, definition } =
      FACTOR_SCORE_KEYS.find((entry) => entry.key === factor.summaryLetter) || {};
    return {
      key,
      scoreKey: factor.summaryLetter,
      letter: summaryLetterOf(factor),
      label: factor.label,
      fullLabel,
      definition,
      complete: totals.complete,
      scored,
      total,
      max: totals.max,
      value: total === null ? null : scorePercent(total, totals.max),
      display: total === null ? NOT_SCORED_LABEL : `${total} / ${totals.max}`,
    };
  });
}

/**
 * Overall Total Score for the category (Settings — Jobs, Rules 1–2): the
 * straight, unweighted sum of the four factor scores against the fixed model
 * maximum (73 points). Unscored subfactors contribute 0, so a partly scored
 * category reports the raw points entered so far. There is no percentage form;
 * `total` is null only until the first subfactor is scored.
 */
export function overallTotalScore(normalizedScores) {
  const total = FACTORS.reduce((sum, factor) => sum + factorTotal(normalizedScores, factor), 0);
  const scored = hasScoredSubfactor(normalizedScores);
  return {
    total: scored ? total : null,
    max: MAX_TOTAL_SCORE,
  };
}

/** True when at least one subfactor of the category carries a level (level 0 counts as scored). */
function hasScoredSubfactor(normalizedScores) {
  return FACTORS.some((factor) => factorHasScoredSubfactor(normalizedScores, factor));
}

/** The API factor points at `factorScores[letter]`, clamped to the factor maximum; null when missing/invalid. */
function pointsFromFactorScores(factorScores, letter) {
  if (factorScores == null || factorScores[letter] === null || factorScores[letter] === undefined) {
    return null;
  }
  const numeric = Number(factorScores[letter]);
  const entry = FACTOR_SCORE_KEYS.find((factor) => factor.key === letter);
  if (!entry || !Number.isFinite(numeric)) return null;
  return Math.min(entry.max, Math.max(0, Math.round(numeric)));
}

/** The summary points shown beside a factor gauge ("19 / 28" or "– / 28"). */
export function factorSummaryDisplay(row) {
  return row.display ?? "–";
}

/** True when `value` is an integer level within a subfactor's scale (re-exported). */
export function isScored(value, maxLevel) {
  return isScoreSet(value, maxLevel);
}

/** "Level 3 of 5" / empty prompt, mirroring the wizard's subfactor meta line. */
export function levelText(value, maxLevel) {
  return isScoreSet(value, maxLevel)
    ? `Level ${Number(value)} of ${maxLevel}`
    : `Select a level between 0 and ${maxLevel}`;
}

/** "3 / 5" / "Not scored" badge text for a subfactor. */
export function scoreBadge(value, maxLevel) {
  return isScoreSet(value, maxLevel) ? `${Number(value)} / ${maxLevel}` : "Not scored";
}

/** Slider fill geometry: level 0 sits at 0%, the subfactor's maximum at 100%. */
export function sliderFillPercent(value, maxLevel) {
  if (!isScoreSet(value, maxLevel)) return 0;
  const max = Number(maxLevel);
  return max > 0 ? (Number(value) / max) * 100 : 0;
}

/** Figma subfactor description (formal EIGE copy, falls back to the wizard's). */
export function subfactorDescription(subfactor) {
  if (!subfactor || typeof subfactor !== "object") return "";
  return SUBFACTOR_DESCRIPTIONS[subfactor.key] ?? subfactor.description ?? "";
}

/** "Level N. <descriptor>" for a scored subfactor; "" when un-scored. */
export function levelDescriptorText(subfactor, level) {
  if (!subfactor || !isScoreSet(level, subfactorMaxLevel(subfactor))) return "";
  const value = Number(level);
  const descriptor = subfactorLevelDescription(subfactor, value);
  if (!descriptor) return "";
  return `Level ${value}. ${descriptor}`;
}

/**
 * One factor section: totals plus each subfactor's score, level text and the
 * official EIGE level descriptor for the current level.
 */
export function factorSectionView(factor, normalizedScores) {
  const totals = factorTotalsView(factor, normalizedScores);
  return {
    ...totals,
    subfactors: factor.subfactors.map((subfactor) => {
      const score = normalizedScores[factor.key]?.[subfactor.key] ?? null;
      const maxLevel = subfactorMaxLevel(subfactor);
      return {
        key: subfactor.key,
        label: subfactor.label,
        description: subfactorDescription(subfactor),
        score,
        maxLevel,
        scoreLevels: subfactorScoreLevels(subfactor),
        levelText: levelText(score, maxLevel),
        levelDescription: levelDescriptorText(subfactor, score),
        levelTooltips: subfactorLevelTooltips(subfactor),
      };
    }),
  };
}

/** All four factor sections, in the AC order (Skills, Responsibility, Effort, Working Conditions). */
export function factorSections(normalizedScores) {
  return FACTORS.map((factor) => factorSectionView(factor, normalizedScores));
}

/**
 * Whether every required input for the edit state is filled: a non-empty name
 * AND a complete score set (all fifteen subfactors scored). Reuses the create
 * wizard's `wizardIsComplete()` so the two gates cannot drift apart.
 */
export function editIsReady(name, normalizedScores) {
  return wizardIsComplete(name, normalizedScores);
}

/**
 * Factor points (`s/e/r/wc`) from a category's API factor scores (Skills 0–28,
 * Effort 0–15, Responsibility 0–20, Working Conditions 0–10); `null` where the
 * factor is missing/invalid. Points are the only unit the summary reports.
 */
export function categoryFactorScoresOf(category) {
  const source =
    category &&
    typeof category === "object" &&
    category.factorScores &&
    typeof category.factorScores === "object"
      ? category.factorScores
      : {};
  return {
    s: pointsFromFactorScores(source, "s"),
    e: pointsFromFactorScores(source, "e"),
    r: pointsFromFactorScores(source, "r"),
    wc: pointsFromFactorScores(source, "wc"),
  };
}

/**
 * Nested scores for the detail page: the persisted subfactor levels only.
 * There is no reconstruction path — a category without persisted subfactor
 * rows shows unscored sliders rather than fabricated, redistributed levels
 * (ER-11786 removes `distributeFactorPercent` / `scoresFromFactorPercents`).
 */
export function resolvedCategoryScores(category) {
  return normalizeCategoryScores(category);
}

/**
 * Draft factor points: prefer complete nested subfactor totals, otherwise the
 * API factor points (`skills_score` etc.) on their own EIGE maxima.
 */
export function initialDraftFactorScores(category) {
  const scores = resolvedCategoryScores(category);
  const fromApi = categoryFactorScoresOf(category);
  const derived = {};
  FACTORS.forEach((factor) => {
    derived[factor.summaryLetter] = factorIsComplete(scores, factor)
      ? factorTotal(scores, factor)
      : null;
  });
  return {
    s: derived.s ?? fromApi.s,
    e: derived.e ?? fromApi.e,
    r: derived.r ?? fromApi.r,
    wc: derived.wc ?? fromApi.wc,
  };
}

/** Apply a subfactor click and copy the derived points onto that factor when complete. */
export function applyDraftSubfactorScore(
  normalizedScores,
  factorScores,
  factorKey,
  subfactorKey,
  level
) {
  const nextScores = {
    ...normalizedScores,
    [factorKey]: { ...(normalizedScores && normalizedScores[factorKey]), [subfactorKey]: level },
  };
  const factor = factorByKey(factorKey);
  const letter = factor && factor.summaryLetter;
  const nextPoints = { ...(factorScores || {}) };
  if (letter && factorIsComplete(nextScores, factor)) {
    nextPoints[letter] = factorTotal(nextScores, factor);
  }
  return { scores: nextScores, factorScores: nextPoints };
}

/**
 * Payload emitted with the `save` event: the category id, trimmed name and the
 * nested subfactor scores. The factor and total scores are computed by the
 * backend from the levels and are never sent (ER-11935).
 */
export function buildSavePayload(category, name, normalizedScores) {
  const id = category && typeof category === "object" ? category.id ?? null : null;
  return {
    id,
    name: typeof name === "string" ? name.trim() : "",
    scores: normalizedScores,
  };
}
